//NWCRoleSelector.tsx
import React from 'react';
import { isDefinitionalAdjustment, isTradeWorkingCapital, getNWCRoleColorClass } from '../../lib/financial/nwcUtils';
import { AccountType, NWCRole } from '../../types';

interface NWCRoleSelectorProps {
  value: NWCRole | null;
  onChange: (value: NWCRole | null) => void;
  accountType: AccountType;
}

const NWCRoleSelector: React.FC<NWCRoleSelectorProps> = ({ 
  value, 
  onChange,
  accountType
}) => {
  // Determine if this account type should have NWC role options at all
  const isBalanceSheetItem = ['current-asset', 'non-current-asset', 'current-liability', 'non-current-liability', 'equity', 'contra-equity'].includes(accountType);
  
  // Only current assets and liabilities are typically used in NWC calculations
  const isCurrentItem = ['current-asset', 'current-liability'].includes(accountType);
  
  // Only show NWC roles when they make sense for the account type
  // In NWCRoleSelector.tsx
  if (!isBalanceSheetItem || !isCurrentItem) {
    return (
      <div className="text-gray-400 text-xs whitespace-normal flex items-center">
        <svg className="h-3.5 w-3.5 mr-1 flex-shrink-0 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
        <span>Not applicable</span>
      </div>
    );
  }

  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value ? e.target.value as NWCRole : null)}
      className="w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
      aria-label="Select NWC Role"
    >
      <option value="">None</option>
      
      {/* Definitional adjustment options are available for current assets and liabilities */}
      {accountType === 'current-asset' && (
        <optgroup label="Definitional Adjustments">
          <option value="cash">Cash (Adj)</option>
          <option value="debt-like">Debt-Like Item (Adj)</option>
        </optgroup>
      )}
      
      {accountType === 'current-liability' && (
        <optgroup label="Definitional Adjustments">
          <option value="short-term-debt">Short-Term Debt (Adj)</option>
          <option value="debt-like">Debt-Like Item (Adj)</option>
        </optgroup>
      )}
      
      {/* Trade working capital options */}
      {accountType === 'current-asset' && (
        <optgroup label="Trade Working Capital">
          <option value="accounts-receivable">Accounts Receivable (Trade)</option>
          <option value="inventory">Inventory (Trade)</option>
        </optgroup>
      )}
      
      {accountType === 'current-liability' && (
        <optgroup label="Trade Working Capital">
          <option value="accounts-payable">Accounts Payable (Trade)</option>
        </optgroup>
      )}
    </select>
  );
};

export default NWCRoleSelector;
